<script setup lang="ts">
import { useEventListener, useResizeObserver } from '@vueuse/core';
import { ref } from 'vue';

import {
  useCoreStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { AlternativeHotel } from '@apps/main/new-desktop/pages/tripOverview/models';
import { useTripOverviewPanelsStore } from '@apps/main/new-desktop/pages/tripOverview/stores';

import { AppButton, AppImg } from '@libs/ui';

import AlternativeAccommodationCardContent from '../AlternativeAccommodationCardContent.vue';
import AccommodationPrice from '../AccommodationPrice.vue';
import DateCardTag from '../../DateCardTag.vue';

import MobileProductCardLayout from './MobileProductCardLayout.vue';

interface AlternativeHotelCardMobileProps {
  hotel: AlternativeHotel;
  ingredientIndex: number;
  date: string;
  isMetaExperiment: boolean;
}

const props = defineProps<AlternativeHotelCardMobileProps>();

const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();

const footerRef = ref<HTMLElement | null>(null);
const priceRef = ref<HTMLElement | null>(null);
const buttonRef = ref<HTMLElement | null>(null);

const isFooterWrapped = ref<boolean | undefined>(undefined);

function showDetails(shouldScrollToRooms: boolean): void {
  statsigEventsStore.isAlternativeHotelOpened = true;
  statsigEventsStore.logTripOverviewAlternativeHotelsCardClicked();
  tripOverviewPanelsStore.openHotelDetailsPanel(
    props.hotel.hotelCode,
    props.hotel.ingredientId,
    shouldScrollToRooms
  );
}

function calculateIsFooterWrapped(): void {
  if (!footerRef.value || !priceRef.value || !buttonRef.value) {
    return;
  }
  const sideMargin = 8;
  const availableWidth = footerRef.value.clientWidth - sideMargin;
  const totalContentWidth =
    priceRef.value.clientWidth + buttonRef.value.clientWidth;
  isFooterWrapped.value = totalContentWidth >= availableWidth;
}

useEventListener(window, 'resize', calculateIsFooterWrapped);
useResizeObserver([footerRef, priceRef, buttonRef], () => {
  calculateIsFooterWrapped();
});
</script>

<template>
  <MobileProductCardLayout
    class="alternative-hotel-card-mobile"
    @click.stop="showDetails(false)"
  >
    <template #top-section>
      <AppImg
        class="image"
        :src="props.hotel.imgUrls[0] || ''"
        :alt="$t('a11y.hotel_image')"
        @img-load-error="coreStore.handleImageLoadError"
      />
    </template>
    <template #content>
      <AlternativeAccommodationCardContent
        :hotel="props.hotel"
        :ingredient-index="props.ingredientIndex"
        @show-details="showDetails(false)"
        @show-rooms="showDetails(true)"
      />
    </template>
    <template #footer>
      <div
        ref="footerRef"
        class="footer"
        :class="{
          wrapped: isFooterWrapped,
          'meta-experiment': props.isMetaExperiment,
        }"
      >
        <div ref="priceRef">
          <AccommodationPrice :price="props.hotel.deltaPrice" />
        </div>
        <div v-if="!props.isMetaExperiment" ref="buttonRef">
          <AppButton
            type="primary"
            :left-icon="{ name: 'change' }"
            state="enabled"
            size="small"
            @click="
              showDetails(false),
                statsigEventsStore.logTripOverviewChangeAlternateHotelCardClicked()
            "
          >
            {{ $t('overview_accommodation_footer.change') }}
          </AppButton>
        </div>
      </div>
    </template>
    <template #top-section-tag>
      <DateCardTag :date="props.date" />
    </template>
  </MobileProductCardLayout>
</template>

<style scoped lang="scss">
.alternative-hotel-card-mobile {
  position: relative;

  .image {
    aspect-ratio: 4 / 3;
  }

  .content-container {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-16) var(--spacing-12);
    gap: var(--spacing-12);
    justify-content: space-between;
    height: 100%;

    .info-container {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .hotel-name {
        color: var(--color-soft-black);
        @include font-label-m-bold;
        line-height: var(--line-heights-16);
        margin-bottom: var(--spacing-4);

        display: -webkit-box;
        max-width: var(--card-width);
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .polygon-name {
        @include font-label-s-light;
        color: var(--color-neutral-70);
      }
    }

    .rooms-container {
      padding: var(--spacing-16);
      background: var(--color-neutral-10);
      border: 1px solid var(--color-neutral-30);
      border-radius: var(--border-radius-8);
      margin: auto 0 0;

      .row {
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: var(--spacing-8);

        .description {
          @include font-label-s-light;

          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          width: 0;
          min-width: 100%;

          .label {
            @include font-label-s-bold;
            white-space: nowrap;
          }

          &.single-room {
            white-space: normal;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            line-height: var(--line-heights-14);
          }
        }
      }
    }
  }

  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: var(--spacing-8) 0;
    flex-wrap: wrap;
    & > * {
      width: fit-content;
    }

    &.wrapped {
      flex-direction: column;
      justify-content: end;
      align-items: end;
    }

    &.meta-experiment {
      justify-content: flex-end;
    }
  }
}
</style>
